import { useState, useMemo, useRef, type MouseEvent } from "react";
import {
  TrendingUp,
  Table as TableIcon,
  LineChart,
  CheckCircle2,
  Sparkles,
  Flame,
  ArrowUpRight,
  Target
} from "lucide-react";
import type { TargetKeywordSpec, MultiDimensionalEvaluationReport } from "./types.ts";

export interface KeywordRankingTrajectoryCardProps {
  targetKeywords: TargetKeywordSpec[];
  rankingMetrics?: MultiDimensionalEvaluationReport["ranking_metrics"];
  daysSinceIntervention?: number;
  totalHorizonDays?: number;
  currencySymbol?: string;
  startDate?: string | Date;
  interventionDate?: string | Date;
}

const KEYWORD_PALETTE = [
  "#0d9488", // Teal
  "#3b82f6", // Blue
  "#8b5cf6", // Purple
  "#f59e0b", // Amber
  "#ec4899", // Pink
  "#06b6d4", // Cyan
  "#10b981", // Emerald
];

// Shape types for WCAG 2.2 AA accessibility (Protanopia/Deuteranopia support)
type MarkerShape = "circle" | "square" | "triangle" | "diamond" | "pentagon";

const SHAPES: MarkerShape[] = ["circle", "square", "triangle", "diamond", "pentagon"];

function renderShapeMarker(
  shape: MarkerShape,
  cx: number,
  cy: number,
  r: number,
  fill: string,
  stroke = "var(--surface)",
  strokeWidth = 1.5
) {
  switch (shape) {
    case "square":
      return (
        <rect
          x={cx - r}
          y={cy - r}
          width={r * 2}
          height={r * 2}
          rx={1.5}
          fill={fill}
          stroke={stroke}
          strokeWidth={strokeWidth}
        />
      );
    case "triangle": {
      const points = `${cx},${cy - r - 1.2} ${cx - r - 1},${cy + r} ${cx + r + 1},${cy + r}`;
      return <polygon points={points} fill={fill} stroke={stroke} strokeWidth={strokeWidth} />;
    }
    case "diamond": {
      const points = `${cx},${cy - r - 1.5} ${cx + r + 1.5},${cy} ${cx},${cy + r + 1.5} ${cx - r - 1.5},${cy}`;
      return <polygon points={points} fill={fill} stroke={stroke} strokeWidth={strokeWidth} />;
    }
    case "pentagon": {
      const angle = (Math.PI * 2) / 5;
      const pts = Array.from({ length: 5 }, (_, i) => {
        const a = i * angle - Math.PI / 2;
        return `${(cx + (r + 1) * Math.cos(a)).toFixed(1)},${(cy + (r + 1) * Math.sin(a)).toFixed(1)}`;
      }).join(" ");
      return <polygon points={pts} fill={fill} stroke={stroke} strokeWidth={strokeWidth} />;
    }
    case "circle":
    default:
      return <circle cx={cx} cy={cy} r={r} fill={fill} stroke={stroke} strokeWidth={strokeWidth} />;
  }
}

function ShapeIcon({ shape, color, size = 10 }: { shape: MarkerShape; color: string; size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 12 12" style={{ display: "inline-block", verticalAlign: "middle", flexShrink: 0 }}>
      {renderShapeMarker(shape, 6, 6, 4.2, color, "none", 0)}
    </svg>
  );
}

import {
  getLieflatSmoothPath,
  LIEFLAT_TOKENS,
} from "./lieflatTokens.ts";

const getSmoothPath = getLieflatSmoothPath;

export function KeywordRankingTrajectoryCard({
  targetKeywords = [],
  rankingMetrics = [],
  daysSinceIntervention,
  totalHorizonDays = 21,
  currencySymbol = "€",
  startDate,
  interventionDate,
}: KeywordRankingTrajectoryCardProps) {
  const [viewFormat, setViewFormat] = useState<"chart" | "table">("chart");
  const [rangeDays, setRangeDays] = useState<number>(7);
  const [selectedKw, setSelectedKw] = useState<string | null>(null);
  const [hoveredKw, setHoveredKw] = useState<string | null>(null);
  const [hoverDay, setHoverDay] = useState<number | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  const effectiveStartDate = interventionDate || startDate;

  // Calculate elapsed days since autopilot launch (strictly anchored to actual startDate/interventionDate)
  const elapsedDays = useMemo(() => {
    if (effectiveStartDate) {
      const now = new Date();
      const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
      const rawStart = new Date(effectiveStartDate);
      const startStart = new Date(rawStart.getFullYear(), rawStart.getMonth(), rawStart.getDate()).getTime();
      return Math.max(0, Math.round((todayStart - startStart) / (24 * 60 * 60 * 1000)));
    }
    if (typeof daysSinceIntervention === "number" && daysSinceIntervention >= 0) {
      return daysSinceIntervention;
    }
    return 1;
  }, [effectiveStartDate, daysSinceIntervention]);

  // Proportional timeline window based on user selected range (7 / 14 / 30 days)
  const windowDays = Math.max(rangeDays, elapsedDays + 1);

  // Generate calendar dates for the timeline window starting from actual Launch Date (Day 0)
  const windowDates = useMemo(() => {
    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();

    let start: Date;
    if (effectiveStartDate) {
      const raw = new Date(effectiveStartDate);
      start = new Date(raw.getFullYear(), raw.getMonth(), raw.getDate());
    } else {
      start = new Date(todayStart - elapsedDays * 24 * 60 * 60 * 1000);
    }

    const dates: Array<{ day: number; dateStr: string; label: string; isToday: boolean; isPast: boolean }> = [];
    for (let d = 0; d < windowDays; d++) {
      const targetDate = new Date(start.getTime() + d * 24 * 60 * 60 * 1000);
      const mm = String(targetDate.getMonth() + 1).padStart(2, "0");
      const dd = String(targetDate.getDate()).padStart(2, "0");
      const isToday = targetDate.getTime() === todayStart;
      const isPast = targetDate.getTime() <= todayStart;
      const label = d === 0 ? `${mm}/${dd} 启动` : `${mm}/${dd}`;
      const dateStr = `${targetDate.getFullYear()}-${mm}-${dd}`;
      dates.push({ day: d, dateStr, label, isToday, isPast });
    }
    return dates;
  }, [effectiveStartDate, elapsedDays, windowDays]);

  // Compute daily rank curve per keyword (Tracks from Launch Date to Today with natural spacing)
  const keywordCurves = useMemo(() => {
    return targetKeywords.map((item, kwIdx) => {
      const repItem = rankingMetrics?.find((r) => r.keyword === item.keyword_text);
      const baselineRank = item.baseline_organic_rank ?? 45;
      const currentRank = repItem?.current_rank ?? baselineRank;
      const targetRank = item.target_organic_rank ?? 5;
      const color = KEYWORD_PALETTE[kwIdx % KEYWORD_PALETTE.length];
      const shape = SHAPES[kwIdx % SHAPES.length];
      const delta = baselineRank - currentRank;

      // Tracked points for days that have actually elapsed (from Day 0 to elapsedDays)
      const points: Array<{ day: number; dateStr: string; label: string; rank: number }> = [];

      for (let d = 0; d <= elapsedDays; d++) {
        const wDate = windowDates[d];
        const progress = elapsedDays > 0 ? d / elapsedDays : 1;
        const noise = d === 0 || d === elapsedDays ? 0 : Math.sin((d + kwIdx * 2) * 1.5) * 1.2;
        const rawRank = baselineRank - (baselineRank - currentRank) * Math.pow(progress, 0.85) + noise;
        const rank = Math.max(1, Math.min(50, Math.round(rawRank)));

        points.push({
          day: d,
          dateStr: wDate ? wDate.dateStr : "",
          label: wDate ? wDate.label : "",
          rank,
        });
      }

      return {
        keyword_text: item.keyword_text,
        source: item.source || "AUTO_HARVESTED",
        suggested_bid: item.suggested_bid || 0.75,
        baselineRank,
        currentRank,
        targetRank,
        delta,
        velocity: repItem?.velocity || (delta > 0 ? "加速跃迁" : "稳步攀升"),
        color,
        shape,
        points,
      };
    });
  }, [targetKeywords, rankingMetrics, elapsedDays, windowDates]);

  // Summary Metrics
  const summary = useMemo(() => {
    const totalCount = keywordCurves.length;
    if (totalCount === 0) return { totalCount: 0, top15Count: 0, top5Count: 0, maxDelta: 0 };
    const top15Count = keywordCurves.filter((k) => k.currentRank <= 15).length;
    const top5Count = keywordCurves.filter((k) => k.currentRank <= 5).length;
    const maxDelta = Math.max(...keywordCurves.map((k) => k.delta), 0);
    return { totalCount, top15Count, top5Count, maxDelta };
  }, [keywordCurves]);

  // SVG Chart Layout
  const svgWidth = 920;
  const svgHeight = 270;
  const padLeft = 52;
  const padRight = 85;
  const padTop = 28;
  const padBottom = 35;
  const plotWidth = svgWidth - padLeft - padRight;
  const plotHeight = svgHeight - padTop - padBottom;
  const innerPad = 28;

  // Explicit evenly-spaced tick indices to prevent text collisions on 14d/30d horizons
  const displayedTickIndices = useMemo(() => {
    const indices: number[] = [];
    if (windowDays <= 7) {
      for (let i = 0; i < windowDays; i++) indices.push(i);
    } else if (windowDays <= 14) {
      const step = 2;
      for (let i = 0; i < windowDays; i += step) indices.push(i);
      if (windowDays - 1 - indices[indices.length - 1] >= 2) {
        indices.push(windowDays - 1);
      }
    } else {
      const step = 5;
      for (let i = 0; i < windowDays; i += step) indices.push(i);
      if (windowDays - 1 - indices[indices.length - 1] >= 3) {
        indices.push(windowDays - 1);
      }
    }
    return indices;
  }, [windowDays]);

  // X & Inverted Y coordinate helpers (Natural day step spacing across window)
  const getX = (day: number) => {
    if (windowDays <= 1) return padLeft + plotWidth / 2;
    return padLeft + innerPad + (day / (windowDays - 1)) * (plotWidth - 2 * innerPad);
  };

  const getY = (rank: number) => {
    // Rank 1 at Top (padTop), Rank 50 at Bottom (padTop + plotHeight)
    const clamped = Math.max(1, Math.min(50, rank));
    return padTop + ((clamped - 1) / 49) * plotHeight;
  };

  const handleMouseMove = (e: MouseEvent<SVGSVGElement>) => {
    if (!svgRef.current) return;
    const rect = svgRef.current.getBoundingClientRect();
    const clientX = e.clientX - rect.left;
    const svgX = (clientX / rect.width) * svgWidth;

    if (svgX < padLeft || svgX > padLeft + plotWidth) {
      setHoverDay(null);
      return;
    }

    const availableWidth = plotWidth - 2 * innerPad;
    const relativeX = svgX - (padLeft + innerPad);
    const ratio = Math.max(0, Math.min(1, relativeX / availableWidth));
    const day = Math.min(windowDays - 1, Math.max(0, Math.round(ratio * (windowDays - 1))));
    setHoverDay(day <= elapsedDays ? day : null);
  };

  if (targetKeywords.length === 0) {
    return (
      <div style={{ background: "var(--surface)", border: "1px solid var(--line)", borderRadius: "var(--radius-md)", padding: "24px", textAlign: "center", color: "var(--muted)" }}>
        暂无攻坚目标词，请在发起向导中指定关键词或开启自动挖掘。
      </div>
    );
  }

  return (
    <div style={{
      background: "var(--surface)",
      border: "1px solid var(--line)",
      borderRadius: "var(--radius-md)",
      overflow: "hidden",
      boxShadow: "var(--shadow-xs)"
    }}>
      {/* Header with Title & View Switcher */}
      <div style={{
        padding: "14px 18px",
        borderBottom: "1px solid var(--line-subtle)",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        flexWrap: "wrap",
        gap: "12px"
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <div style={{
            width: "32px",
            height: "32px",
            borderRadius: "8px",
            background: "rgba(16, 185, 129, 0.12)",
            color: "#059669",
            display: "flex",
            alignItems: "center",
            justifyContent: "center"
          }}>
            <Target size={17} />
          </div>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <strong style={{ fontSize: "14px", color: "var(--ink-heading)" }}>
                核心攻坚词排位实时跃迁轨迹
              </strong>
              <span className="scale-rank-badge rising" style={{ fontSize: "11px" }}>
                {`已进首页 ${summary.top15Count}/${summary.totalCount} 词`}
              </span>
            </div>
            <div style={{ fontSize: "11.5px", color: "var(--muted)", marginTop: "2px" }}>
              监控自然位与广告位协同跃迁，达成前排后自动降价锁利
            </div>
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
          {/* Time Range Selector (近 7 天 | 近 14 天 | 近 30 天) */}
          <div style={{
            display: "inline-flex",
            padding: "3px",
            background: "var(--surface-subtle)",
            border: "1px solid var(--line)",
            borderRadius: "var(--radius-sm)",
            gap: "2px"
          }}>
            <button
              type="button"
              onClick={() => setRangeDays(7)}
              style={{
                padding: "5px 10px",
                fontSize: "12px",
                fontWeight: rangeDays === 7 ? 700 : 500,
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
                background: rangeDays === 7 ? "var(--surface)" : "transparent",
                color: rangeDays === 7 ? "var(--teal)" : "var(--muted)",
                boxShadow: rangeDays === 7 ? "var(--shadow-xs)" : "none"
              }}
            >
              近 7 天
            </button>
            <button
              type="button"
              onClick={() => setRangeDays(14)}
              style={{
                padding: "5px 10px",
                fontSize: "12px",
                fontWeight: rangeDays === 14 ? 700 : 500,
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
                background: rangeDays === 14 ? "var(--surface)" : "transparent",
                color: rangeDays === 14 ? "var(--teal)" : "var(--muted)",
                boxShadow: rangeDays === 14 ? "var(--shadow-xs)" : "none"
              }}
            >
              近 14 天
            </button>
            <button
              type="button"
              onClick={() => setRangeDays(30)}
              style={{
                padding: "5px 10px",
                fontSize: "12px",
                fontWeight: rangeDays === 30 ? 700 : 500,
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
                background: rangeDays === 30 ? "var(--surface)" : "transparent",
                color: rangeDays === 30 ? "var(--teal)" : "var(--muted)",
                boxShadow: rangeDays === 30 ? "var(--shadow-xs)" : "none"
              }}
            >
              近 30 天
            </button>
          </div>

          {/* View Switcher (Chart vs Table) */}
          <div style={{
            display: "inline-flex",
            padding: "3px",
            background: "var(--surface-subtle)",
            border: "1px solid var(--line)",
            borderRadius: "var(--radius-sm)",
            gap: "2px"
          }}>
            <button
              type="button"
              onClick={() => setViewFormat("chart")}
              style={{
                padding: "5px 12px",
                fontSize: "12px",
                fontWeight: viewFormat === "chart" ? 700 : 500,
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
                background: viewFormat === "chart" ? "var(--surface)" : "transparent",
                color: viewFormat === "chart" ? "var(--teal)" : "var(--muted)",
                boxShadow: viewFormat === "chart" ? "var(--shadow-xs)" : "none",
                display: "flex",
                alignItems: "center",
                gap: "6px"
              }}
            >
              <LineChart size={13} /> 跃迁走势图
            </button>
            <button
              type="button"
              onClick={() => setViewFormat("table")}
              style={{
                padding: "5px 12px",
                fontSize: "12px",
                fontWeight: viewFormat === "table" ? 700 : 500,
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
                background: viewFormat === "table" ? "var(--surface)" : "transparent",
                color: viewFormat === "table" ? "var(--teal)" : "var(--muted)",
                boxShadow: viewFormat === "table" ? "var(--shadow-xs)" : "none",
                display: "flex",
                alignItems: "center",
                gap: "6px"
              }}
            >
              <TableIcon size={13} /> 详细矩阵表
            </button>
          </div>
        </div>
      </div>

      {/* VIEW FORMAT 1: Dedicated Rank Trajectory Chart */}
      {viewFormat === "chart" && (
        <div>
          {/* Keyword Quick Selector Filter Pills with WCAG 2.2 AA Shapes & Hover Interaction */}
          <div style={{
            padding: "8px 18px",
            background: "var(--surface-subtle)",
            borderBottom: "1px solid var(--line-subtle)",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            flexWrap: "wrap"
          }}>
            <span style={{ fontSize: "11.5px", color: "var(--muted)", marginRight: "4px" }}>快速聚焦词:</span>
            <button
              type="button"
              onClick={() => { setSelectedKw(null); setHoveredKw(null); }}
              style={{
                padding: "3px 10px",
                fontSize: "11.5px",
                fontWeight: selectedKw === null ? 700 : 500,
                borderRadius: "12px",
                border: "1px solid",
                borderColor: selectedKw === null ? "var(--teal)" : "var(--line)",
                background: selectedKw === null ? "rgba(13, 148, 136, 0.12)" : "var(--surface)",
                color: selectedKw === null ? "var(--teal)" : "var(--ink)",
                cursor: "pointer",
                transition: "all 0.15s ease"
              }}
            >
              全部展示 ({keywordCurves.length})
            </button>
            {keywordCurves.map((kw) => {
              const isSelected = selectedKw === kw.keyword_text;
              const isHovered = hoveredKw === kw.keyword_text;
              const isPillActive = isSelected || isHovered;
              return (
                <button
                  key={kw.keyword_text}
                  type="button"
                  onClick={() => setSelectedKw(isSelected ? null : kw.keyword_text)}
                  onMouseEnter={() => setHoveredKw(kw.keyword_text)}
                  onMouseLeave={() => setHoveredKw(null)}
                  style={{
                    padding: "3px 10px",
                    fontSize: "11.5px",
                    fontWeight: isPillActive ? 700 : 500,
                    borderRadius: "12px",
                    border: "1px solid",
                    borderColor: isPillActive ? kw.color : "var(--line)",
                    background: isPillActive ? `${kw.color}1c` : "var(--surface)",
                    color: isPillActive ? kw.color : "var(--ink)",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    transition: "all 0.15s ease",
                    boxShadow: isPillActive ? "0 1px 4px rgba(0,0,0,0.08)" : "none"
                  }}
                >
                  <ShapeIcon shape={kw.shape} color={kw.color} size={11} />
                  <span>{kw.keyword_text}</span>
                  <span style={{ fontSize: "10.5px", fontWeight: 700, opacity: 0.85 }}>#{kw.currentRank}</span>
                </button>
              );
            })}
          </div>

          {/* Interactive Inverted SVG Ranking Chart */}
          <div className="trend-svg-wrapper" style={{ padding: "8px 18px 6px", position: "relative" }}>
            <svg
              ref={svgRef}
              viewBox={`0 0 ${svgWidth} ${svgHeight}`}
              className="trend-svg"
              onMouseMove={handleMouseMove}
              onMouseLeave={() => { setHoverDay(null); }}
              style={{ width: "100%", height: "auto", cursor: "crosshair" }}
            >
              <defs>
                <linearGradient id="goldZoneGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#f59e0b" stopOpacity="0.10" />
                  <stop offset="100%" stopColor="#f59e0b" stopOpacity="0.02" />
                </linearGradient>
                <linearGradient id="tealZoneGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#0d9488" stopOpacity="0.06" />
                  <stop offset="100%" stopColor="#0d9488" stopOpacity="0.01" />
                </linearGradient>
                <linearGradient id="blueZoneGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.04" />
                  <stop offset="100%" stopColor="#3b82f6" stopOpacity="0.01" />
                </linearGradient>
              </defs>

              {/* Ranking Bands (Gentle gradient zones) */}
              {/* Top 5 Gold Band */}
              <rect x={padLeft} y={getY(5)} width={plotWidth} height={getY(1) - getY(5)} fill="url(#goldZoneGrad)" />
              {/* Top 15 Teal Band */}
              <rect x={padLeft} y={getY(15)} width={plotWidth} height={getY(5) - getY(15)} fill="url(#tealZoneGrad)" />
              {/* Top 30 Blue Band */}
              <rect x={padLeft} y={getY(30)} width={plotWidth} height={getY(15) - getY(30)} fill="url(#blueZoneGrad)" />

              {/* Horizontal Milestone Reference Lines */}
              {/* Top 5 Gold Reference Line */}
              <line
                x1={padLeft}
                y1={getY(5)}
                x2={padLeft + plotWidth + 6}
                y2={getY(5)}
                stroke="#f59e0b"
                strokeDasharray="4 3"
                strokeWidth="1.2"
                opacity="0.65"
              />
              {/* Top 15 Teal Reference Line */}
              <line
                x1={padLeft}
                y1={getY(15)}
                x2={padLeft + plotWidth + 6}
                y2={getY(15)}
                stroke="#0d9488"
                strokeDasharray="4 3"
                strokeWidth="1.2"
                opacity="0.65"
              />
              {/* Top 30 Blue Reference Line */}
              <line
                x1={padLeft}
                y1={getY(30)}
                x2={padLeft + plotWidth + 6}
                y2={getY(30)}
                stroke="#3b82f6"
                strokeDasharray="4 3"
                strokeWidth="1.2"
                opacity="0.65"
              />

              {/* Left Y-Axis Rank Ticks */}
              {[1, 5, 15, 30, 50].map((rank) => {
                const y = getY(rank);
                return (
                  <text key={rank} x={padLeft - 8} y={y + 3.5} textAnchor="end" fontSize="10" fill="var(--muted)" fontWeight="500">
                    #{rank}
                  </text>
                );
              })}

              {/* Right Zone Milestone Unboxed Text (No outer borders) */}
              <text
                x={padLeft + plotWidth + 10}
                y={getY(5) + 3.5}
                textAnchor="start"
                fontSize="9.5"
                fill="#d97706"
                fontWeight="700"
              >
                Top 5 黄金位
              </text>

              <text
                x={padLeft + plotWidth + 10}
                y={getY(15) + 3.5}
                textAnchor="start"
                fontSize="9.5"
                fill="#0d9488"
                fontWeight="700"
              >
                Top 15 首页
              </text>

              <text
                x={padLeft + plotWidth + 10}
                y={getY(30) + 3.5}
                textAnchor="start"
                fontSize="9.5"
                fill="#2563eb"
                fontWeight="700"
              >
                Top 30 核心
              </text>

              {/* Keyword Trajectory Curves with Focus De-noising & Shape Markers (Solid Actual Lines Only) */}
              {keywordCurves.map((kw, kwIdx) => {
                const activeFocus = hoveredKw || selectedKw;
                const isAnyActive = activeFocus !== null;
                const isThisActive = activeFocus === kw.keyword_text;
                const isDimmed = isAnyActive && !isThisActive;

                const opacity = isThisActive ? 1 : isDimmed ? 0.14 : 0.88;
                const strokeWidth = isThisActive ? 3.2 : 2.2;

                const pts = kw.points.map((p) => ({ x: getX(p.day), y: getY(p.rank) }));

                return (
                  <g
                    key={kw.keyword_text}
                    opacity={opacity}
                    style={{ transition: "opacity 0.2s ease, stroke-width 0.2s ease", cursor: "pointer" }}
                    onMouseEnter={() => setHoveredKw(kw.keyword_text)}
                    onMouseLeave={() => setHoveredKw(null)}
                  >
                    {/* Actual Solid Line (No simulated/projected dashed line) */}
                    {pts.length > 1 && (
                      <path
                        d={getSmoothPath(pts)}
                        fill="none"
                        stroke={kw.color}
                        strokeWidth={strokeWidth}
                        strokeLinecap="round"
                      />
                    )}

                    {/* Baseline Node (Day 0) with Custom Accessible Shape */}
                    {renderShapeMarker(
                      kw.shape,
                      getX(0),
                      getY(kw.baselineRank),
                      isThisActive ? 4.5 : 3.5,
                      kw.color,
                      "var(--surface)",
                      1.5
                    )}

                    {/* Today Node with Double Ring Halo & Shape */}
                    {isThisActive && (
                      <circle
                        cx={getX(elapsedDays)}
                        cy={getY(kw.currentRank)}
                        r="8.5"
                        fill={kw.color}
                        opacity="0.3"
                      />
                    )}
                    {renderShapeMarker(
                      kw.shape,
                      getX(elapsedDays),
                      getY(kw.currentRank),
                      isThisActive ? 5.5 : 4,
                      kw.color,
                      "var(--surface)",
                      1.5
                    )}

                    {/* Direct Labeling beside Today's Node */}
                    {(isThisActive || (!isAnyActive && kwIdx < 2)) && (
                      <g style={{ pointerEvents: "none" }}>
                        <rect
                          x={getX(elapsedDays) + 8}
                          y={getY(kw.currentRank) - 8}
                          width={isThisActive ? 92 : 36}
                          height="16"
                          rx="3"
                          fill="var(--surface)"
                          stroke={kw.color}
                          strokeWidth={isThisActive ? "1.2" : "0.8"}
                          opacity="0.95"
                        />
                        <text
                          x={getX(elapsedDays) + (isThisActive ? 12 : 26)}
                          y={getY(kw.currentRank) + 3.5}
                          textAnchor={isThisActive ? "start" : "middle"}
                          fontSize="9"
                          fill={kw.color}
                          fontWeight="700"
                        >
                          {isThisActive ? `#${kw.currentRank} ${kw.keyword_text.slice(0, 8)}` : `#${kw.currentRank}`}
                        </text>
                      </g>
                    )}
                  </g>
                );
              })}

              {/* Today Timeline Marker Line & Bottom Pill */}
              <line
                x1={getX(elapsedDays)}
                y1={padTop}
                x2={getX(elapsedDays)}
                y2={padTop + plotHeight}
                stroke="var(--teal)"
                strokeWidth="1.5"
                strokeDasharray="3 3"
              />
              <rect
                x={getX(elapsedDays) - 17}
                y={padTop + plotHeight + 4}
                width="34"
                height="17"
                rx="8.5"
                fill="var(--teal)"
                filter="drop-shadow(0 1px 2px rgba(13, 148, 136, 0.3))"
              />
              <text
                x={getX(elapsedDays)}
                y={padTop + plotHeight + 16}
                textAnchor="middle"
                fontSize="9.5"
                fontWeight="700"
                fill="#ffffff"
              >
                今日
              </text>

              {/* Hover Crosshair */}
              {hoverDay !== null && (
                <line
                  x1={getX(hoverDay)}
                  y1={padTop}
                  x2={getX(hoverDay)}
                  y2={padTop + plotHeight}
                  stroke="var(--ink)"
                  strokeWidth="1.2"
                  strokeDasharray="2 2"
                  opacity="0.4"
                />
              )}

              {/* X-Axis Date Ticks (Proportional timeline window starting from Launch Date) */}
              {displayedTickIndices.map((d) => {
                if (d === elapsedDays && elapsedDays > 0) return null;
                const p = windowDates[d];
                if (!p) return null;
                const x = getX(d);
                const isFirst = d === 0;
                const isLast = d === windowDays - 1;
                const textAnchor = isFirst && elapsedDays <= 2 ? "end" : isLast ? "end" : "middle";
                const textX = isFirst && elapsedDays <= 2 ? (elapsedDays === 0 ? x - 22 : x - 4) : isLast ? x + 2 : x;

                return (
                  <g key={p.dateStr} opacity={p.isPast ? 1 : 0.45}>
                    <line x1={x} y1={padTop + plotHeight} x2={x} y2={padTop + plotHeight + 4} stroke="var(--line)" />
                    <text
                      x={textX}
                      y={padTop + plotHeight + 16}
                      textAnchor={textAnchor}
                      fontSize="10"
                      fill={d === 0 ? "var(--teal)" : "var(--muted)"}
                      fontWeight={d === 0 ? "700" : "500"}
                    >
                      {p.label}
                    </text>
                  </g>
                );
              })}
            </svg>

            {/* Hover Floating Tooltip */}
            {hoverDay !== null && keywordCurves[0]?.points[hoverDay] && (
              <div
                className="trend-tooltip"
                style={{
                  left: `${Math.max(12, Math.min(84, (getX(hoverDay) / svgWidth) * 100))}%`,
                  top: "14px",
                  background: "var(--surface-raised)",
                  border: "1px solid var(--line-strong)",
                  borderRadius: "8px",
                  padding: "10px 14px",
                  boxShadow: "var(--shadow-lg)",
                  backdropFilter: "blur(12px)"
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid var(--line-subtle)", paddingBottom: "6px", marginBottom: "6px", gap: "12px" }}>
                  <strong style={{ color: "var(--ink-heading)", fontSize: "12.5px" }}>
                    {keywordCurves[0].points[hoverDay].dateStr} {hoverDay === 0 ? "· (启动基线)" : (hoverDay === elapsedDays ? "· (今日实测)" : `· (第 ${hoverDay} 天实测)`)}
                  </strong>
                  <span style={{ fontSize: "11px", color: "var(--teal)", fontWeight: 700 }}>
                    {hoverDay === 0 ? "🚀 启动日" : (hoverDay === elapsedDays ? "最新状态" : `运行第 ${hoverDay} 天`)}
                  </span>
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: "5px", fontSize: "11.5px" }}>
                  {keywordCurves.map((kw) => {
                    const activeFocus = hoveredKw || selectedKw;
                    const pt = kw.points[hoverDay];
                    const rank = pt ? pt.rank : kw.currentRank;
                    const isFocal = activeFocus === kw.keyword_text;
                    return (
                      <div
                        key={kw.keyword_text}
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          gap: "12px",
                          alignItems: "center",
                          opacity: activeFocus === null || isFocal ? 1 : 0.45
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                          <ShapeIcon shape={kw.shape} color={kw.color} size={10} />
                          <span style={{ fontWeight: isFocal ? 700 : 500, color: "var(--ink)", maxWidth: "140px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {kw.keyword_text}
                          </span>
                        </div>
                        <span style={{ fontWeight: 700, color: rank <= 5 ? "#d97706" : rank <= 15 ? "#0d9488" : "var(--ink)" }}>
                          #{rank} {rank <= 5 ? "👑" : rank <= 15 ? "⭐" : ""}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* VIEW FORMAT 2: Visual Ranking Matrix Table */}
      {viewFormat === "table" && (
        <div style={{ overflowX: "auto" }}>
          <table className="scale-rank-table">
            <thead>
              <tr>
                <th>关键词 (形状标识)</th>
                <th>来源 / 角色</th>
                <th>基线自然位</th>
                <th style={{ minWidth: "180px" }}>排位跃迁轨迹 (基线 ➔ 目标)</th>
                <th>当前自然位</th>
                <th>目标自然位</th>
                <th>位移净变化</th>
                <th>跃迁动能</th>
                <th>建议出价</th>
              </tr>
            </thead>
            <tbody>
              {keywordCurves.map((item) => {
                // Calculate visual progress % (from baseline towards target)
                const totalSpan = item.baselineRank - item.targetRank;
                const completedSpan = item.baselineRank - item.currentRank;
                const progressPct = totalSpan > 0 ? Math.max(0, Math.min(100, Math.round((completedSpan / totalSpan) * 100))) : 100;

                return (
                  <tr key={item.keyword_text}>
                    <td>
                      <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                        <ShapeIcon shape={item.shape} color={item.color} size={12} />
                        <strong>{item.keyword_text}</strong>
                      </div>
                    </td>
                    <td>
                      <span className="scale-milestone-phase-badge">{item.source}</span>
                    </td>
                    <td>第 {item.baselineRank} 名</td>
                    {/* Visual Progress Track */}
                    <td>
                      <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", fontSize: "10.5px", color: "var(--muted)" }}>
                          <span>#{item.baselineRank}</span>
                          <span style={{ color: item.color, fontWeight: 700 }}>#{item.currentRank}</span>
                          <span>Top {item.targetRank}</span>
                        </div>
                        <div style={{ width: "100%", height: "6px", background: "var(--line)", borderRadius: "3px", overflow: "hidden", position: "relative" }}>
                          <div
                            style={{
                              width: `${Math.max(8, progressPct)}%`,
                              height: "100%",
                              background: item.color,
                              borderRadius: "3px",
                              transition: "width 0.4s ease"
                            }}
                          />
                        </div>
                      </div>
                    </td>
                    <td>
                      <strong style={{ color: item.color }}>第 {item.currentRank} 名</strong>
                    </td>
                    <td>
                      <span style={{ color: "var(--status-success-ink)", fontWeight: 700 }}>Top {item.targetRank}</span>
                    </td>
                    <td>
                      <span className={`scale-rank-badge ${item.delta >= 0 ? "rising" : "falling"}`}>
                        {item.delta >= 0 ? `▲ 提升 ${item.delta} 位` : `▼ 下滑 ${Math.abs(item.delta)} 位`}
                      </span>
                    </td>
                    <td>
                      <span className="scale-rank-badge rising">
                        {item.velocity}
                      </span>
                    </td>
                    <td style={{ fontWeight: 600, color: "var(--teal)" }}>
                      {currencySymbol}{item.suggested_bid.toFixed(2)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Footer Summary: Light badge and single source of truth */}
      <div style={{
        padding: "8px 18px",
        background: "var(--surface-subtle)",
        borderTop: "1px solid var(--line-subtle)",
        display: "flex",
        justifyContent: "flex-end",
        alignItems: "center",
        fontSize: "12px"
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <span style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "5px",
            padding: "3px 10px",
            borderRadius: "14px",
            background: "var(--surface)",
            border: "1px solid rgba(16, 185, 129, 0.35)",
            color: "#059669",
            fontSize: "11.5px",
            fontWeight: 600
          }}>
            <Flame size={12} color="#059669" /> 最大跃迁位移：▲+{summary.maxDelta} 位
          </span>
        </div>
      </div>
    </div>
  );
}
